import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import ButtonGroupContext from 'ui-library/buttons/ButtonGroupContext';

interface ButtonGroupProps {
  children: React.ReactNode;
  isFullWidth?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

/**
 * Group of buttons that are presented side-by-side with spacing and corners adjusted to make them a group.
 *
 * To use, just render multiple <Button>s and <ButtonGroupAddon>s as children.
 *
 * `isFullWidth={true}`:
 *  - causes the ButtonGroup to expand to fill its container.
 *  - causes <Button> and <ButtonGroupAddon> children to render with `flex: 1`. To disable this on specific
 *    children, specify `flexWidth={false}` on the child.
 *  - is required when using <ButtonGroupAddon> for flexbox support.
 */
export default function ButtonGroup({
  children,
  isFullWidth = false,
  dataTestAttrs,
}: ButtonGroupProps): JSX.Element {
  // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
  const buttonGroupContext = {inFullWidthButtonGroup: isFullWidth};
  return (
    <ButtonGroupContext.Provider value={buttonGroupContext}>
      <div
        className={classNames('buttonGroup', {[css(styles.fullWidthButtonGroup)]: isFullWidth})}
        {...getTestProps(
          dataTestAttrs ?? {
            component: 'ButtonGroup',
            element: 'root',
          }
        )}
      >
        {children}
      </div>
    </ButtonGroupContext.Provider>
  );
}

/**
 * Container for <inputs> so that it can be placed into a button group.
 *
 * Ensures that the direct-child <input> as well as sibling <Button> elements have square corners.
 * Implemented via CSS (see: less/components/button-group.less).
 */
export function ButtonGroupAddon({
  children,
  flexWidth,
}: {
  children: React.ReactNode;
  flexWidth?: boolean;
}): JSX.Element {
  return (
    <ButtonGroupContext.Consumer>
      {({inFullWidthButtonGroup}) => (
        <div
          className={classNames('buttonGroup-addon', {
            [css(styles.flexWidthButtonGroupAddon)]: flexWidth ?? inFullWidthButtonGroup,
          })}
        >
          {children}
        </div>
      )}
    </ButtonGroupContext.Consumer>
  );
}

const styles = StyleSheet.create({
  fullWidthButtonGroup: {
    display: 'flex',
    width: '100%',
  },
  flexWidthButtonGroupAddon: {
    flex: '1',
  },
});
